import type { ReactNode } from 'react';

// Small form primitives shared by the template editor sections. They exist so
// every question in the questionnaire reads the same way: a label, the
// answer control, and — when the answer is one the runner cannot replay — a
// note saying so right under it, not in a footer far away.

export const inputCls = 'w-full px-3 py-2 rounded-md bg-background border border-border text-content text-sm focus:outline-none focus:ring-2 focus:ring-primary/40';

export function Field({ label, hint, children, className = '' }: { label: ReactNode; hint?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <div className={className}>
      <div className="text-sm text-content mb-1.5">{label}</div>
      {children}
      {hint && <p className="text-xs text-content-muted mt-1.5">{hint}</p>}
    </div>
  );
}

export function SectionTitle({ children, sub }: { children: ReactNode; sub?: ReactNode }) {
  return (
    <div className="mb-4">
      <h3 className="text-base font-semibold text-content">{children}</h3>
      {sub && <p className="text-sm text-content-muted mt-1">{sub}</p>}
    </div>
  );
}

export function Group({ title, children, className = '' }: { title?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-lg border border-border bg-surface p-4 ${className}`}>
      {title && <div className="text-xs font-medium uppercase tracking-wide text-content-subtle mb-3">{title}</div>}
      <div className="space-y-4">{children}</div>
    </div>
  );
}

export interface ChoiceOption<T extends string> { value: T; label: ReactNode; disabled?: boolean; title?: string }

/** A segmented control: one of a few answers, all visible at once. */
export function Choice<T extends string>({ value, options, onChange, size = 'md' }: {
  value: T; options: ChoiceOption<T>[]; onChange: (v: T) => void; size?: 'sm' | 'md';
}) {
  const pad = size === 'sm' ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-sm';
  return (
    <div className="inline-flex rounded-md border border-border bg-background p-0.5" role="radiogroup">
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={o.disabled}
            title={o.title}
            onClick={() => onChange(o.value)}
            className={`${pad} rounded transition-colors font-medium whitespace-nowrap
              ${active ? 'bg-primary text-white shadow-sm' : 'text-content-muted hover:text-content hover:bg-surface-muted'}
              disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function YesNo({ value, onChange, size }: { value: boolean; onChange: (v: boolean) => void; size?: 'sm' | 'md' }) {
  return (
    <Choice<'yes' | 'no'>
      size={size}
      value={value ? 'yes' : 'no'}
      onChange={(v) => onChange(v === 'yes')}
      options={[{ value: 'no', label: 'No' }, { value: 'yes', label: 'Yes' }]}
    />
  );
}

/** A labelled row with the control on the right: what a questionnaire looks like. */
export function QuestionRow({ label, hint, children, note }: { label: ReactNode; hint?: ReactNode; children: ReactNode; note?: ReactNode }) {
  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-sm text-content">{label}</div>
          {hint && <div className="text-xs text-content-muted mt-0.5">{hint}</div>}
        </div>
        <div className="shrink-0">{children}</div>
      </div>
      {note && <Note>{note}</Note>}
    </div>
  );
}

/** Says why an answer will not run hands-off. Shown where the answer is. */
export function Note({ children, tone = 'warn' }: { children: ReactNode; tone?: 'warn' | 'info' }) {
  const cls = tone === 'warn'
    ? 'bg-amber-500/10 border-amber-500/30 text-amber-400'
    : 'bg-primary/5 border-primary/30 text-content-muted';
  return <div className={`mt-2 px-3 py-2 rounded-md border text-xs ${cls}`}>{children}</div>;
}

export interface CheckOption<T extends string> { value: T; label: ReactNode; hint?: ReactNode; disabled?: boolean }

export function CheckList<T extends string>({ value, options, onChange, columns = 1 }: {
  value: T[]; options: CheckOption<T>[]; onChange: (v: T[]) => void; columns?: 1 | 2 | 3;
}) {
  const toggle = (v: T) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  const grid = columns === 3 ? 'sm:grid-cols-3' : columns === 2 ? 'sm:grid-cols-2' : '';
  return (
    <div className={`grid grid-cols-1 ${grid} gap-1`}>
      {options.map((o) => (
        <label
          key={o.value}
          className={`flex items-start gap-2.5 px-2 py-1.5 rounded-md cursor-pointer hover:bg-surface-muted ${o.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <input
            type="checkbox"
            className="mt-0.5 accent-primary"
            checked={value.includes(o.value)}
            disabled={o.disabled}
            onChange={() => toggle(o.value)}
          />
          <span className="min-w-0">
            <span className="text-sm text-content">{o.label}</span>
            {o.hint && <span className="block text-xs text-content-muted">{o.hint}</span>}
          </span>
        </label>
      ))}
    </div>
  );
}

export function Switch({ checked, onChange, label, hint, disabled }: {
  checked: boolean; onChange: (v: boolean) => void; label: ReactNode; hint?: ReactNode; disabled?: boolean;
}) {
  return (
    <label className={`flex items-start justify-between gap-4 ${disabled ? 'opacity-50' : 'cursor-pointer'}`}>
      <span className="min-w-0">
        <span className="text-sm text-content">{label}</span>
        {hint && <span className="block text-xs text-content-muted mt-0.5">{hint}</span>}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`relative shrink-0 w-10 h-6 rounded-full transition-colors ${checked ? 'bg-primary' : 'bg-border-strong'}`}
      >
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4' : ''}`} />
      </button>
    </label>
  );
}

export function Problems({ items }: { items: string[] }) {
  if (items.length === 0) return null;
  return (
    <ul className="px-3 py-2 rounded-md bg-red-500/10 border border-red-500/30 text-sm text-red-400 space-y-1">
      {items.map((m) => <li key={m}>{m}</li>)}
    </ul>
  );
}
